import React, { useState } from "react";
import { X, PlusCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const DISMISSAL_TYPES = ["Not Out", "Caught", "Bowled", "LBW", "Run Out", "Stumped", "Hit Wicket"];
const BOWLER_TYPES = ["Pace", "Fast-Medium", "Medium", "Off-Spin", "Leg-Spin", "Left-Arm Spin", "Left-Arm Pace"];
const FORMATS = ["T20", "ODI", "Test", "T10"];
const MATCH_PHASES = ["Full Match", "Powerplay Cameo", "Middle Consolidation", "Death Finisher"];

export default function MatchLogModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(() => ({
    date: new Date().toISOString().split("T")[0],
    opponent: "",
    format: "T20",
    venue: "",
    match_phase: "Full Match",
    runs: 0, balls: 0, fours: 0, sixes: 0,
    dismissal_type: "Not Out", bowler_type: "Pace",
    pp_runs: 0, mid_runs: 0, death_runs: 0,
    overs: 0, runs_conceded: 0, wickets: 0, dot_balls: 0,
    bowl_pp: 0, bowl_mid: 0, bowl_death: 0,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => Number(v) || 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      date: form.date,
      opponent: form.opponent || "Unnamed Opposition",
      format: form.format,
      venue: form.venue || "Home Ground",
      match_phase: form.match_phase,
      batting: {
        runs: num(form.runs), balls: num(form.balls),
        fours: num(form.fours), sixes: num(form.sixes),
        dismissal_type: form.dismissal_type,
        bowler_type: form.bowler_type,
        phase_breakdown: {
          powerplay: num(form.pp_runs),
          middle: num(form.mid_runs),
          death: num(form.death_runs),
        },
      },
      bowling: {
        overs: num(form.overs),
        runs_conceded: num(form.runs_conceded),
        wickets: num(form.wickets),
        dot_balls: num(form.dot_balls),
        phase_breakdown: {
          powerplay: num(form.bowl_pp),
          middle: num(form.bowl_mid),
          death: num(form.bowl_death),
        },
      },
    };
    try {
      const { data } = await api.post("/matches", payload);
      toast.success(`Logged ${payload.batting.runs} vs ${payload.opponent}`);
      try { confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 } }); } catch (_) {}
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#141414] border border-[#262626] rounded-2xl max-w-3xl w-full p-6 md:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[#262626] pb-4">
          <div>
            <h3 className="text-xl font-bold uppercase text-white flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-[#FF3B30]" /> Log Match Statistics
            </h3>
            <p className="text-xs text-[#A3A3A3]">Batting, bowling and phase breakdown</p>
          </div>
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="text-[#A3A3A3] hover:text-white p-2 rounded hover:bg-[#1A1A1A]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Opponent Team">
              <input data-testid="input-opponent" required type="text" value={form.opponent}
                onChange={(e) => upd("opponent", e.target.value)}
                placeholder="e.g. Melbourne Warriors"
                className={inputCls} />
            </Field>
            <Field label="Match Format">
              <select data-testid="input-format" value={form.format} onChange={(e) => upd("format", e.target.value)} className={inputCls}>
                {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Match Date">
              <input data-testid="input-date" type="date" value={form.date} onChange={(e) => upd("date", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Venue">
              <input data-testid="input-venue" type="text" value={form.venue} onChange={(e) => upd("venue", e.target.value)} placeholder="e.g. MCG Oval 2" className={inputCls} />
            </Field>
            <Field label="Match Phase Role">
              <select data-testid="input-phase" value={form.match_phase} onChange={(e) => upd("match_phase", e.target.value)} className={inputCls}>
                {MATCH_PHASES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>

          <div className="border-t border-[#262626] pt-4 space-y-3">
            <h4 className="text-sm font-bold uppercase text-white">Batting Innings</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SmallField label="Runs" testid="input-runs" value={form.runs} onChange={(v) => upd("runs", v)} />
              <SmallField label="Balls Faced" testid="input-balls" value={form.balls} onChange={(v) => upd("balls", v)} />
              <SmallField label="Fours (4s)" testid="input-fours" value={form.fours} onChange={(v) => upd("fours", v)} />
              <SmallField label="Sixes (6s)" testid="input-sixes" value={form.sixes} onChange={(v) => upd("sixes", v)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Dismissal Type">
                <select data-testid="input-dismissal" value={form.dismissal_type} onChange={(e) => upd("dismissal_type", e.target.value)} className={inputCls}>
                  {DISMISSAL_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Bowler Type Faced">
                <select data-testid="input-bowler-type" value={form.bowler_type} onChange={(e) => upd("bowler_type", e.target.value)} className={inputCls}>
                  {BOWLER_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <SmallField label="PP Runs" testid="input-pp" value={form.pp_runs} onChange={(v) => upd("pp_runs", v)} />
              <SmallField label="Middle Runs" testid="input-mid" value={form.mid_runs} onChange={(v) => upd("mid_runs", v)} />
              <SmallField label="Death Runs" testid="input-death" value={form.death_runs} onChange={(v) => upd("death_runs", v)} />
            </div>
          </div>

          <div className="border-t border-[#262626] pt-4 space-y-3">
            <h4 className="text-sm font-bold uppercase text-white">Bowling Figures (optional)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SmallField label="Overs Bowled" testid="input-overs" step="0.1" value={form.overs} onChange={(v) => upd("overs", v)} />
              <SmallField label="Runs Conceded" testid="input-conceded" value={form.runs_conceded} onChange={(v) => upd("runs_conceded", v)} />
              <SmallField label="Wickets" testid="input-wickets" value={form.wickets} onChange={(v) => upd("wickets", v)} />
              <SmallField label="Dot Balls" testid="input-dots" value={form.dot_balls} onChange={(v) => upd("dot_balls", v)} />
            </div>
          </div>

          {error && (
            <div data-testid="modal-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-xs rounded px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#262626]">
            <button
              type="button"
              data-testid="modal-cancel-btn"
              onClick={onClose}
              className="px-4 py-2 rounded bg-[#1A1A1A] text-[#A3A3A3] hover:text-white text-sm font-semibold border border-[#262626]"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="modal-save-btn"
              disabled={busy}
              className="px-6 py-2 rounded bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white text-sm font-bold shadow-lg"
            >
              {busy ? "Saving..." : "Save Match"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF3B30]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}

function SmallField({ label, testid, value, onChange, step = "1" }) {
  return (
    <div>
      <label className="block text-[11px] font-mono text-[#A3A3A3] uppercase mb-1">{label}</label>
      <input
        data-testid={testid}
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#FF3B30]"
      />
    </div>
  );
}
